/* "Claim this tracker" strip on the public pump tracker — turns a share link into a real
   partner account that can see EVERY supplier the pump company works for, not just this one. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Handshake, Loader2, X } from "lucide-react";
import { BACKEND_ORIGIN } from "@/lib/api";

export default function ClaimPumpAccount({ token, pumpName }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ company: pumpName || "", name: "", email: "", phone: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${BACKEND_ORIGIN}/api/public/pump/${token}/claim`, { ...f, kind: "pump" });
      toast.success("Partner account created — sign in to the Partner Portal");
      window.location.href = "/contractor/login";
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not create the account"); }
    finally { setBusy(false); }
  };

  const field = "w-full bg-transparent border border-industrial focus:border-primary-orange outline-none px-3 py-2.5 mono text-sm text-ink placeholder:text-muted-industrial";

  if (!open) {
    return (
      <div className="max-w-3xl mx-auto px-4 mt-3">
        <button type="button" onClick={() => setOpen(true)} data-testid="pump-claim-open"
          className="w-full border border-primary-orange/60 bg-primary-orange/10 px-4 py-3 text-left hover:bg-primary-orange/20">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-primary-orange font-black inline-flex items-center gap-1.5">
            <Handshake className="w-3.5 h-3.5" /> Free partner account
          </div>
          <div className="text-sm mt-1">
            Claim this tracker and see <b>every</b> supplier you work for in one live screen — and invite the rest of them.
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 mt-3">
      <form onSubmit={submit} className="card-industrial p-4 space-y-3" data-testid="pump-claim-form">
        <div className="flex items-center gap-2">
          <div className="heading text-lg font-black uppercase">Claim your partner account</div>
          <button type="button" onClick={() => setOpen(false)} data-testid="pump-claim-close" className="ml-auto p-1.5 border border-industrial text-muted-industrial hover:text-white"><X className="w-3.5 h-3.5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Pump company" value={f.company} onChange={set("company")} data-testid="pump-claim-company" className={field} />
          <input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="pump-claim-name" className={field} />
          <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="pump-claim-email" className={field} />
          <input placeholder="Mobile" value={f.phone} onChange={set("phone")} data-testid="pump-claim-phone" className={field} />
          <input required type="password" placeholder="Password (8+ with a number)" value={f.password} onChange={set("password")} data-testid="pump-claim-password" className={`${field} sm:col-span-2`} />
        </div>
        <button type="submit" disabled={busy} data-testid="pump-claim-submit"
          className="w-full mono uppercase tracking-widest font-black bg-primary-orange text-black py-3 hover:bg-[rgb(var(--primary-hover))] disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4" />} Create my partner account
        </button>
      </form>
    </div>
  );
}
